import { forwardRef } from 'react';
import sfx from './sfx';
import './Button.css';

/* ============================================================================
   Button — the single pressable surface for the whole app.

   Every clickable thing routes through here so the press physics, the sheen,
   the hover lift and the sound hooks stay identical everywhere.

   variant: primary | secondary | neon | gold | ghost | danger | link
   size:    sm | md | lg | xl
   ========================================================================== */

const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    block = false,
    tilt = 0,
    icon = null,
    iconRight = null,
    sound = 'click',
    hoverSound = 'hover',
    className = '',
    onClick,
    onMouseEnter,
    type = 'button',
    ...rest
  },
  ref
) {
  const handleClick = (event) => {
    if (sound) sfx.play(sound);
    onClick?.(event);
  };

  const handleMouseEnter = (event) => {
    if (hoverSound) sfx.play(hoverSound);
    onMouseEnter?.(event);
  };

  const classes = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    block ? 'ui-btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      style={tilt ? { '--btn-tilt': `${tilt}deg` } : undefined}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      {...rest}
    >
      {/* Light sweep that runs across the face on hover. */}
      <span className="ui-btn__sheen" aria-hidden="true" />
      <span className="ui-btn__label">
        {icon && <span className="ui-btn__icon">{icon}</span>}
        {children}
        {iconRight && <span className="ui-btn__icon">{iconRight}</span>}
      </span>
    </button>
  );
});

export default Button;

/* Square, chrome-only variant for HUD toggles. Shares the same press physics. */
export const IconButton = forwardRef(function IconButton(
  { children, label, className = '', active = false, ...rest },
  ref
) {
  return (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      className={`ui-btn--icon ${active ? 'is-active' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
});
